import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MessageSquare, X, Send, Sparkles, Wine, Shirt, ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { API } from "../lib/api";

const LABELS = {
  de: { title: "MYSYMP Concierge", sub: "Ihr digitaler Gastgeber", placeholder: "Ihre Frage …", send: "Senden", open: "Concierge öffnen", error: "Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.", leadTitle: "Gerne melden wir uns bei Ihnen", namePh: "Name", emailPh: "E-Mail", msgPh: "Ihr Anliegen (optional)", leadSubmit: "Absenden", leadCancel: "Später", leadSuccess: "Vielen Dank — wir melden uns in Kürze bei Ihnen.", leadErr: "Bitte Name und gültige E-Mail angeben.", winePairing: "Zu Wine Pairing", popstyle: "Zu POPSTYLE" },
  en: { title: "MYSYMP Concierge", sub: "Your digital host", placeholder: "Your question …", send: "Send", open: "Open concierge", error: "Sorry, something went wrong. Please try again.", leadTitle: "We'd be glad to get back to you", namePh: "Name", emailPh: "Email", msgPh: "Your enquiry (optional)", leadSubmit: "Submit", leadCancel: "Later", leadSuccess: "Thank you — we will be in touch shortly.", leadErr: "Please provide a name and a valid email.", winePairing: "Go to Wine Pairing", popstyle: "Go to POPSTYLE" },
  fr: { title: "MYSYMP Concierge", sub: "Votre hôte numérique", placeholder: "Votre question …", send: "Envoyer", open: "Ouvrir le concierge", error: "Désolé, une erreur s'est produite. Veuillez réessayer.", leadTitle: "Nous vous recontacterons volontiers", namePh: "Nom", emailPh: "E-mail", msgPh: "Votre demande (optionnel)", leadSubmit: "Envoyer", leadCancel: "Plus tard", leadSuccess: "Merci — nous vous recontactons très prochainement.", leadErr: "Veuillez indiquer un nom et un e-mail valide.", winePairing: "Vers Wine Pairing", popstyle: "Vers POPSTYLE" },
};

const LEAD_MARKER = /\[\[LEAD_FORM\]\]/g;
const WINE_MARKER = /\[\[WINE_PAIRING\]\]/g;
const POPSTYLE_MARKER = /\[\[POPSTYLE\]\]/g;
const WINE_URL = "https://wine-pairing.online";
const POPSTYLE_URL = "https://popstyle.ai";
const SEEN_KEY = "mysymp_concierge_seen";

const SESSION_KEY = "mysymp_concierge_session";
const getSession = () => {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) { s = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())); localStorage.setItem(SESSION_KEY, s); }
  return s;
};

// Derive a page key from the (basename-stripped) pathname for a context-aware opener.
const pageKeyFrom = (pathname) => {
  const seg = (pathname || "/").split("/").filter(Boolean)[0];
  return seg || "home";
};

// Lightweight markdown → React (bold, headings, bullets, links). Internal links use SPA navigation.
const renderInline = (text, navigate, keyPrefix) => {
  const nodes = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g;
  let last = 0, m, i = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      const label = m[1], href = m[2];
      const internal = href.startsWith("/");
      nodes.push(
        <a key={`${keyPrefix}-l${i}`} href={href}
          onClick={internal ? (e) => { e.preventDefault(); navigate(href); } : undefined}
          target={internal ? undefined : "_blank"} rel={internal ? undefined : "noopener noreferrer"}
          className="text-champagne underline decoration-champagne/40 underline-offset-2 hover:decoration-champagne">
          {label}
        </a>
      );
    } else if (m[3] !== undefined) {
      nodes.push(<strong key={`${keyPrefix}-b${i}`} className="font-medium text-white">{m[3]}</strong>);
    }
    last = regex.lastIndex; i++;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
};

const Markdown = ({ text, navigate }) => (
  <div className="space-y-1.5 text-[13.5px] leading-relaxed">
    {text.split("\n").map((line, idx) => {
      const t = line.trim();
      if (t === "" || t === "---") return null;
      if (t.startsWith("### ")) return <h4 key={idx} className="mt-2 font-medium text-white">{renderInline(t.slice(4), navigate, `h${idx}`)}</h4>;
      if (t.startsWith("## ")) return <h3 key={idx} className="mt-2 font-serif text-base text-champagne">{renderInline(t.slice(3), navigate, `h${idx}`)}</h3>;
      if (t.startsWith("- ") || t.startsWith("• ") || t.startsWith("* ")) return <li key={idx} className="ml-4 list-disc text-white/80">{renderInline(t.slice(2), navigate, `li${idx}`)}</li>;
      if (t.startsWith("→ ")) return <p key={idx} className="text-white/80">{renderInline(t, navigate, `p${idx}`)}</p>;
      return <p key={idx} className="text-white/80">{renderInline(t, navigate, `p${idx}`)}</p>;
    })}
  </div>
);

const ConciergeWidget = () => {
  const { lang } = useLang();
  const L = LABELS[lang] || LABELS.de;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [greeted, setGreeted] = useState(false);
  const [leadState, setLeadState] = useState("none"); // none | form | sent
  const [lead, setLead] = useState({ name: "", email: "", message: "" });
  const [leadSending, setLeadSending] = useState(false);
  const [chips, setChips] = useState([]);
  const [showChips, setShowChips] = useState(false);
  const scrollRef = useRef(null);
  const session = useRef(getSession());

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open, leadState]);

  // Subtle auto-open once per visitor after 8s (skip on shop/checkout pages where it overlaps the cart)
  useEffect(() => {
    if (localStorage.getItem(SEEN_KEY)) return;
    if (pathname === "/shop" || pathname.startsWith("/payment")) return;
    const timer = setTimeout(() => {
      if (!localStorage.getItem(SEEN_KEY)) { localStorage.setItem(SEEN_KEY, "1"); openWidget(); }
    }, 8000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const openWidget = async () => {
    setOpen(true);
    localStorage.setItem(SEEN_KEY, "1");
    if (!greeted && messages.length === 0) {
      setGreeted(true);
      try {
        const [hist, greet] = await Promise.all([
          fetch(`${API}/concierge/history/${session.current}`).then((r) => r.json()),
          fetch(`${API}/concierge/greeting?lang=${lang}&page=${pageKeyFrom(pathname)}`).then((r) => r.json()),
        ]);
        if (hist?.messages?.length) { setMessages(hist.messages); }
        else { setMessages([{ role: "assistant", content: greet.greeting }]); setChips(greet.chips || []); setShowChips((greet.chips || []).length > 0); }
      } catch {
        setMessages([{ role: "assistant", content: L.error }]);
      }
    }
  };

  const send = async (textArg) => {
    const text = (typeof textArg === "string" ? textArg : input).trim();
    if (!text || loading) return;
    setInput("");
    setShowChips(false);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setLoading(true);
    let streaming = false;
    const setLast = (content) => setMessages((m) => {
      const c = [...m]; c[c.length - 1] = { role: "assistant", content }; return c;
    });
    try {
      const res = await fetch(`${API}/concierge/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: session.current, message: text, lang }),
      });
      if (!res.ok || !res.body) throw new Error("bad");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "", full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data:\s?/, "").trim();
          if (!line) continue;
          let evt; try { evt = JSON.parse(line); } catch { continue; }
          if (evt.delta) {
            if (!streaming) { streaming = true; setLoading(false); }
            full += evt.delta;
            setLast(full);
          } else if (evt.error) {
            setLast(L.error);
          } else if (evt.done) {
            if (LEAD_MARKER.test(full)) { LEAD_MARKER.lastIndex = 0; if (leadState !== "sent") setLeadState("form"); }
          }
        }
      }
      if (!full) setLast(L.error);
    } catch {
      setLast(L.error);
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async () => {
    if (!lead.name.trim() || !/.+@.+\..+/.test(lead.email)) return;
    setLeadSending(true);
    try {
      const res = await fetch(`${API}/concierge/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: session.current, lang, ...lead }),
      });
      if (!res.ok) throw new Error("bad");
      setLeadState("sent");
      setMessages((m) => [...m, { role: "assistant", content: L.leadSuccess }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: L.error }]);
    } finally {
      setLeadSending(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <>
      {/* Launcher */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="launcher"
            initial={{ opacity: 0, scale: 0.7, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.7, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 24 }}
            onClick={openWidget}
            data-testid="concierge-launcher"
            aria-label={L.open}
            className="fixed bottom-6 right-6 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-champagne text-obsidian shadow-[0_8px_30px_rgba(212,175,55,0.35)] transition-transform hover:scale-105"
          >
            <MessageSquare size={22} strokeWidth={1.8} />
            <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-obsidian">
              <Sparkles size={9} className="text-champagne" />
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            data-testid="concierge-panel"
            className="fixed bottom-6 right-6 z-[60] flex h-[560px] max-h-[calc(100vh-3rem)] w-[calc(100vw-3rem)] max-w-[400px] flex-col border border-champagne/25 bg-[#0b0b0b]/95 shadow-2xl backdrop-blur-xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/10 bg-white/[0.02] px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-champagne/15 text-champagne">
                  <Sparkles size={16} />
                </span>
                <div>
                  <p className="font-serif text-[15px] leading-tight text-white">{L.title}</p>
                  <p className="text-[11px] uppercase tracking-widest text-champagne/70">{L.sub}</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="concierge-close" aria-label="Close" className="text-white/45 transition-colors hover:text-white">
                <X size={20} />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5" data-testid="concierge-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div data-testid={`concierge-msg-${m.role}`}
                    className={`max-w-[85%] px-4 py-2.5 ${m.role === "user"
                      ? "bg-champagne/90 text-obsidian"
                      : "border border-white/10 bg-white/[0.03] text-white/85"}`}>
                    {m.role === "user"
                      ? <p className="text-[13.5px] leading-relaxed">{m.content}</p>
                      : (m.content
                        ? <>
                            <Markdown text={m.content.replace(LEAD_MARKER, "").replace(WINE_MARKER, "").replace(POPSTYLE_MARKER, "").trim()} navigate={navigate} />
                            {(m.content.includes("[[WINE_PAIRING]]") || m.content.includes("wine-pairing.online")) && (
                              <a href={WINE_URL} target="_blank" rel="noopener noreferrer" data-testid="wine-pairing-btn"
                                className="mt-3 inline-flex items-center gap-2 bg-champagne px-4 py-2 text-[11px] font-medium uppercase tracking-widest text-obsidian transition-colors hover:bg-champagne-light">
                                <Wine size={14} /> {L.winePairing} <ArrowRight size={13} />
                              </a>
                            )}
                            {(m.content.includes("[[POPSTYLE]]") || m.content.includes("popstyle.ai")) && (
                              <a href={POPSTYLE_URL} target="_blank" rel="noopener noreferrer" data-testid="popstyle-btn"
                                className="mt-3 inline-flex items-center gap-2 bg-champagne px-4 py-2 text-[11px] font-medium uppercase tracking-widest text-obsidian transition-colors hover:bg-champagne-light">
                                <Shirt size={14} /> {L.popstyle} <ArrowRight size={13} />
                              </a>
                            )}
                          </>
                        : <span className="flex gap-1.5 py-1" data-testid="concierge-loading">
                            {[0, 1, 2].map((d) => (
                              <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-champagne/70" style={{ animationDelay: `${d * 0.15}s` }} />
                            ))}
                          </span>)}
                  </div>
                </div>
              ))}
              {showChips && !loading && chips.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1" data-testid="concierge-chips">
                  {chips.map((c) => (
                    <button key={c} onClick={() => send(c)} data-testid="concierge-chip"
                      className="rounded-full border border-champagne/40 bg-champagne/[0.06] px-3.5 py-1.5 text-[12px] text-champagne transition-colors hover:bg-champagne/15">
                      {c}
                    </button>
                  ))}
                </div>
              )}

              {leadState === "form" && (
                <div className="border border-champagne/30 bg-champagne/[0.04] p-4" data-testid="concierge-lead-form">
                  <p className="mb-3 font-serif text-[15px] text-white">{L.leadTitle}</p>
                  <div className="space-y-2.5">
                    <input value={lead.name} onChange={(e) => setLead((s) => ({ ...s, name: e.target.value }))}
                      placeholder={L.namePh} data-testid="lead-name"
                      className="w-full border border-white/15 bg-black/30 px-3 py-2 text-[13.5px] text-white placeholder:text-white/35 outline-none focus:border-champagne" />
                    <input type="email" value={lead.email} onChange={(e) => setLead((s) => ({ ...s, email: e.target.value }))}
                      placeholder={L.emailPh} data-testid="lead-email"
                      className="w-full border border-white/15 bg-black/30 px-3 py-2 text-[13.5px] text-white placeholder:text-white/35 outline-none focus:border-champagne" />
                    <textarea rows={2} value={lead.message} onChange={(e) => setLead((s) => ({ ...s, message: e.target.value }))}
                      placeholder={L.msgPh} data-testid="lead-message"
                      className="w-full resize-none border border-white/15 bg-black/30 px-3 py-2 text-[13.5px] text-white placeholder:text-white/35 outline-none focus:border-champagne" />
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button onClick={submitLead} disabled={leadSending || !lead.name.trim() || !/.+@.+\..+/.test(lead.email)}
                      data-testid="lead-submit"
                      className="bg-champagne px-4 py-2 text-[11px] uppercase tracking-widest text-obsidian transition-colors hover:bg-champagne-light disabled:opacity-40">
                      {leadSending ? "…" : L.leadSubmit}
                    </button>
                    <button onClick={() => setLeadState("none")} data-testid="lead-cancel"
                      className="px-4 py-2 text-[11px] uppercase tracking-widest text-white/50 hover:text-white">
                      {L.leadCancel}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-white/10 bg-white/[0.02] p-3">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  rows={1}
                  placeholder={L.placeholder}
                  data-testid="concierge-input"
                  className="max-h-28 flex-1 resize-none bg-transparent px-2 py-2 text-[14px] text-white placeholder:text-white/35 outline-none"
                />
                <button onClick={send} disabled={loading || !input.trim()} data-testid="concierge-send" aria-label={L.send}
                  className="flex h-10 w-10 shrink-0 items-center justify-center bg-champagne text-obsidian transition-colors hover:bg-champagne-light disabled:opacity-40">
                  <Send size={17} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ConciergeWidget;
